import React, { useMemo, useState, useCallback, useEffect } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import applicationApi from "src/domains/application/module/api/applicationApi";
import type { BuildLogJob } from "src/domains/application/module/api/IApplicationApi";

import { type DagNodeData, type DagStatus, STATUS_COLORS } from "./dag-types";
import {
  derivePersistedExecutionSteps,
  deriveSteps,
  parsePipelineLogRef,
  type TaskStep,
} from "./job-detail-helpers";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LogViewerProps {
  /** Application alias used for scoped sub-step log retrieval. */
  alias: string;
  /** Selected logical pipeline run identity. */
  sagaId: string | null;
  /** The selected DAG node data (phase + service + status). */
  nodeData: DagNodeData | null;
  /** Persisted sub-step selected from the DAG. */
  selectedExecutionId: number | null;
  /** Git commit SHA for the sub-line. */
  sha: string | null;
  /** Live log batches from useDeploymentStream (rusha_managed). */
  liveLogs: Map<string, Map<string, string[]>>;
  /** REST build log jobs (historical / CI). */
  buildLogJobs: BuildLogJob[];
  /** CI quality gate jobs. */
  qualityGates: BuildLogJob[];
  /** Deployment events scoped to the selected run + step. */
  stepEvents: DeploymentEvent[];
  /** GitHub Actions run URL (for the "No steps" fallback). */
  runHtmlUrl?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusLabel(s: DagStatus): string {
  switch (s) {
    case "success":
      return "Passed";
    case "failed":
      return "Failed";
    case "running":
      return "Running";
    case "skipped":
      return "Skipped";
    default:
      return "Pending";
  }
}

function stepStatusLabel(s: TaskStep["status"]): string {
  switch (s) {
    case "completed":
      return "Passed";
    case "failed":
      return "Failed";
    case "in_progress":
      return "Running";
    case "skipped":
      return "Skipped";
    case "queued":
      return "Queued";
    default:
      return "Pending";
  }
}

function StepIcon({
  status,
}: {
  status: TaskStep["status"];
}): React.ReactElement {
  switch (status) {
    case "completed":
      return <CheckIcon sx={{ fontSize: 16, color: STATUS_COLORS.success }} />;
    case "failed":
      return <CloseIcon sx={{ fontSize: 16, color: STATUS_COLORS.failed }} />;
    case "in_progress":
      return (
        <SyncIcon
          sx={{
            fontSize: 16,
            color: STATUS_COLORS.running,
            animation: `${spin} 1.5s linear infinite`,
          }}
        />
      );
    case "skipped":
      return (
        <Typography sx={{ fontSize: 12, color: STATUS_COLORS.skipped }}>
          –
        </Typography>
      );
    default:
      return (
        <HourglassEmptyIcon
          sx={{ fontSize: 16, color: STATUS_COLORS.pending }}
        />
      );
  }
}

/** Detect log line level from text. */
function lineLevel(text: string): "error" | "warning" | "info" {
  const lower = text.toLowerCase();
  if (lower.includes("error") || lower.includes("✕") || lower.includes("fatal"))
    return "error";
  if (lower.includes("warn") || lower.includes("caution")) return "warning";
  return "info";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LogViewer({
  alias,
  sagaId,
  nodeData,
  selectedExecutionId,
  sha,
  liveLogs,
  buildLogJobs,
  qualityGates,
  stepEvents,
  runHtmlUrl,
}: LogViewerProps): React.ReactElement {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [showTimes, setShowTimes] = useState(true);
  const [raw, setRaw] = useState(false);
  const [openSteps, setOpenSteps] = useState<Set<number>>(new Set());
  const [copied, setCopied] = useState(false);
  const [executionLogLines, setExecutionLogLines] = useState<
    Map<number, string[]>
  >(new Map());
  const [executionLogError, setExecutionLogError] = useState<string | null>(
    null,
  );
  const [executionLogLoading, setExecutionLogLoading] = useState(false);

  useEffect(() => {
    if (!nodeData || selectedExecutionId === null) {
      setExecutionLogLines(new Map());
      setExecutionLogError(null);
      setExecutionLogLoading(false);
      return;
    }

    const execution = nodeData.executions.find(
      (candidate) => candidate.id === selectedExecutionId,
    );
    if (!execution || !execution.logRef || !sagaId) {
      setExecutionLogLines(new Map([[selectedExecutionId, []]]));
      setExecutionLogError(
        execution?.logRef
          ? "This sub-step is not associated with the selected run."
          : "No log is available for this sub-step.",
      );
      setExecutionLogLoading(false);
      return;
    }

    const ref = parsePipelineLogRef(execution.logRef, sagaId);
    if (!ref) {
      setExecutionLogLines(new Map([[selectedExecutionId, []]]));
      setExecutionLogError(
        "This sub-step has an invalid or mismatched log reference.",
      );
      setExecutionLogLoading(false);
      return;
    }

    let cancelled = false;
    setExecutionLogLines(new Map([[selectedExecutionId, []]]));
    setExecutionLogError(null);
    setExecutionLogLoading(true);
    void applicationApi
      .getBuildLogs(alias, ref.service, {
        sagaId,
        phase: ref.phase,
      })
      .then((result) => {
        if (cancelled) return;
        setExecutionLogLines(
          new Map([
            [selectedExecutionId, result.logs.flatMap((log) => log.logLines)],
          ]),
        );
        setExecutionLogError(
          result.logs.length === 0 && result.message ? result.message : null,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setExecutionLogError("Failed to load logs for this sub-step.");
        }
      })
      .finally(() => {
        if (!cancelled) setExecutionLogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [alias, nodeData, sagaId, selectedExecutionId]);

  // Derive steps for the selected job.
  const steps = useMemo<TaskStep[]>(() => {
    if (!nodeData) return [];
    const service = nodeData.service || "";
    // Map phase to the step key expected by deriveSteps.
    const stepKey =
      nodeData.phase === "deploy" || nodeData.phase === "verify"
        ? "deploy"
        : "build";
    if (nodeData.executions.length > 0) {
      const persistedLines = buildLogJobs
        .flatMap((job) => job.logLines)
        .filter((line, index, lines) => lines.indexOf(line) === index);
      return derivePersistedExecutionSteps(
        nodeData.executions,
        persistedLines,
        executionLogLines,
      );
    }
    return deriveSteps(
      liveLogs,
      service,
      stepKey,
      buildLogJobs,
      qualityGates,
      stepEvents,
    );
  }, [
    nodeData,
    liveLogs,
    buildLogJobs,
    qualityGates,
    stepEvents,
    executionLogLines,
  ]);

  // Auto-expand steps that have logs, and always expand failed steps.
  // Re-run when nodeData changes (job selection).
  React.useEffect(() => {
    const open = new Set<number>();
    steps.forEach((step, i) => {
      if (step.status === "failed") open.add(i);
      else if (step.logLines && step.logLines.length > 0) open.add(i);
    });
    setOpenSteps(open);
  }, [steps]);

  const toggleStep = useCallback((index: number) => {
    setOpenSteps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  // Search query (lowercased).
  const query = search.trim().toLowerCase();

  // Filter + highlight log lines.
  const filteredSteps = useMemo(() => {
    if (!query) return steps;
    return steps.map((step) => ({
      ...step,
      logLines: (step.logLines ?? []).filter((line) =>
        line.toLowerCase().includes(query),
      ),
    }));
  }, [steps, query]);

  // Copy all visible log lines.
  const handleCopy = useCallback(() => {
    const lines: string[] = [];
    for (const step of steps) {
      if (!step.logLines || step.logLines.length === 0) continue;
      lines.push(`> ${step.label}`);
      for (const line of step.logLines) {
        lines.push(line);
      }
    }
    const text = lines.join("\n");
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [steps]);

  if (!nodeData) {
    return (
      <Box
        sx={{
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">Select a job to view its logs.</Typography>
      </Box>
    );
  }

  const jobLabel = nodeData.service
    ? `${nodeData.phase} · ${nodeData.service}`
    : nodeData.phase;

  return (
    <Box
      sx={{
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 2,
        bgcolor: (t) => t.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: STATUS_COLORS[nodeData.status],
              flexShrink: 0,
              animation:
                nodeData.status === "running"
                  ? `${spin} 1s linear infinite`
                  : undefined,
            }}
          />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
            {jobLabel}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
            {nodeData.duration ?? statusLabel(nodeData.status)}
            {sha && ` · ${sha.length > 8 ? sha.slice(0, 8) : sha}`}
          </Typography>
        </Stack>

        {/* Toolbar */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <TextField
            size="small"
            placeholder="Search logs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: 180,
              "& .MuiOutlinedInput-root": { fontSize: 12, height: 32 },
            }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearch("")}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Button
            size="small"
            variant={showTimes ? "contained" : "outlined"}
            onClick={() => setShowTimes((v) => !v)}
            sx={{
              textTransform: "none",
              fontSize: 11,
              minWidth: "auto",
              px: 1,
            }}
          >
            Timestamps
          </Button>
          <Button
            size="small"
            variant={raw ? "contained" : "outlined"}
            onClick={() => setRaw((v) => !v)}
            sx={{
              textTransform: "none",
              fontSize: 11,
              minWidth: "auto",
              px: 1,
            }}
          >
            Raw
          </Button>
          <Tooltip title={copied ? "Copied!" : "Copy logs"}>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Step accordions */}
      <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
        {steps.length === 0 ? (
          <Box sx={{ p: 2, color: "text.secondary" }}>
            {runHtmlUrl ? (
              <Typography variant="body2">
                No detailed logs available for this run.{" "}
                <a
                  href={runHtmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                  }}
                >
                  View on GitHub →
                </a>
              </Typography>
            ) : (
              <Typography variant="body2">
                {nodeData.status === "skipped"
                  ? "Job was skipped."
                  : nodeData.status === "pending"
                    ? "Waiting to start…"
                    : "No steps for this job."}
              </Typography>
            )}
          </Box>
        ) : (
          steps.map((step, i) => {
            const open = openSteps.has(i) || query.length > 0;
            const lines = filteredSteps[i]?.logLines ?? [];
            const hasLogs = (step.logLines ?? []).length > 0;
            const isSelectedExecution =
              selectedExecutionId !== null &&
              nodeData.executions[i]?.id === selectedExecutionId;
            return (
              <Box
                key={step.key + i}
                sx={{
                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  title={step.logRef ?? undefined}
                  onClick={() => hasLogs && toggleStep(i)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: hasLogs ? "pointer" : "default",
                    "&:hover": hasLogs
                      ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }
                      : undefined,
                  }}
                >
                  <StepIcon status={step.status} />
                  <Typography sx={{ fontSize: 8, color: "text.disabled" }}>
                    {open ? "▼" : "▶"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}
                    noWrap
                  >
                    {step.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    {step.completedAt && step.startedAt
                      ? (() => {
                          const ms =
                            new Date(step.completedAt).getTime() -
                            new Date(step.startedAt).getTime();
                          const sec = Math.round(ms / 1000);
                          return sec < 60
                            ? `${sec}s`
                            : `${Math.floor(sec / 60)}m ${sec % 60}s`;
                        })()
                      : stepStatusLabel(step.status)}
                  </Typography>
                  {step.htmlUrl && (
                    <IconButton
                      size="small"
                      href={step.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25 }}
                    >
                      <OpenInNewIcon
                        sx={{ fontSize: 14, color: "text.secondary" }}
                      />
                    </IconButton>
                  )}
                </Stack>
                <Collapse in={open}>
                  <Box
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      lineHeight: 1.6,
                      bgcolor: (t) => alpha(t.palette.common.black, 0.03),
                      p: 1.5,
                      maxHeight: 240,
                      overflowY: "auto",
                    }}
                  >
                    {isSelectedExecution && executionLogLoading ? (
                      <Typography
                        sx={{ color: "text.secondary", fontStyle: "italic" }}
                      >
                        Loading sub-step logs…
                      </Typography>
                    ) : executionLogError && isSelectedExecution ? (
                      <Typography
                        sx={{ color: "error.main", fontStyle: "italic" }}
                      >
                        {executionLogError}
                      </Typography>
                    ) : hasLogs ? (
                      lines.length === 0 ? (
                        <Typography
                          sx={{ color: "text.disabled", fontStyle: "italic" }}
                        >
                          No logs match &ldquo;{search}&rdquo;.
                        </Typography>
                      ) : (
                        lines.map((line, li) => {
                          const lv = lineLevel(line);
                          const color =
                            lv === "error"
                              ? STATUS_COLORS.failed
                              : lv === "warning"
                                ? "#f59e0b"
                                : theme.palette.text.primary;
                          // Highlight search match.
                          let rendered: React.ReactNode = line;
                          if (query) {
                            const idx = line.toLowerCase().indexOf(query);
                            if (idx >= 0) {
                              rendered = (
                                <>
                                  {line.slice(0, idx)}
                                  <mark
                                    style={{
                                      background: alpha("#fbbf24", 0.4),
                                      borderRadius: 2,
                                    }}
                                  >
                                    {line.slice(idx, idx + query.length)}
                                  </mark>
                                  {line.slice(idx + query.length)}
                                </>
                              );
                            }
                          }
                          return (
                            <Box key={li} sx={{ display: "flex", gap: 1 }}>
                              {!raw && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "text.disabled",
                                    flexShrink: 0,
                                    width: 32,
                                    textAlign: "right",
                                    userSelect: "none",
                                  }}
                                >
                                  {li + 1}
                                </Typography>
                              )}
                              {showTimes && !raw && step.startedAt && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "text.disabled",
                                    flexShrink: 0,
                                    width: 70,
                                    userSelect: "none",
                                  }}
                                >
                                  {new Date(step.startedAt)
                                    .toISOString()
                                    .slice(11, 19)}
                                </Typography>
                              )}
                              <Typography
                                component="span"
                                sx={{
                                  color,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {rendered}
                              </Typography>
                            </Box>
                          );
                        })
                      )
                    ) : step.htmlUrl ? (
                      <Typography sx={{ fontSize: 12 }}>
                        <a
                          href={step.htmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: theme.palette.primary.main,
                            textDecoration: "none",
                          }}
                        >
                          View on GitHub →
                        </a>
                      </Typography>
                    ) : (
                      <Typography
                        sx={{ color: "text.disabled", fontStyle: "italic" }}
                      >
                        {step.status === "skipped"
                          ? "Job was skipped."
                          : "Waiting to start…"}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
